import ReactMarkdown from "react-markdown";
import { CustomMindMapNode } from "./types";
import { toolsList } from "@packages/utils";
import { useTranslation } from "react-i18next";
import { Text } from "@core/text";
import { LoaderPinwheel, X } from "lucide-react";
import { Button } from "@core/button";
import { GenerateToolFromNodeRequestDtoTargetToolType } from "@packages/api/madlenAPI.schemas";
import LoadingContainer from "@packages/components/Loading";

import DetailCard from "./DetailCard";
const apiAvailableTools = [
  "VOCABULARY_LIST",
  "TRANSFER_ACTIVITY",
  "REAL_WORLD_CONNECTION",
  "PROJECT_BASED_LEARNING",
  "PERFORMANCE_TASK",
  "LESSON_HOOK",
  "PODCAST",
  "HOMEWORK_IDEA",
  "FORMATIVE_ASSESSMENT",
  "EXIT_TICKET_GENERATOR",
  "ESSENTIAL_QUESTIONS",
  "COMMON_MISCONCEPTION",
  "COLLABORATIVE_GROUP_WORK",
  "AI_ASSIGNMENT",
  "AI_ASSESSMENT_SCALE",
  "AI_RESISTANT_HOMEWORK",
  "AI_SUPPORTED_TASKS",
];
export const NodeDescriptionModal = ({
  closeDescriptionModal,
  selectedNodeData,
  generatingToolId,
  generateToolForNode,
}: {
  closeDescriptionModal: () => void;
  selectedNodeData: CustomMindMapNode;
  generatingToolId: { id: number; type: string } | null;
  generateToolForNode: (nodeId: string, toolId: string) => void;
}) => {
  const { t } = useTranslation();
  const allTools = toolsList.flatMap((category) =>
    category.tools.map((tool) => ({
      id: tool.toolType as GenerateToolFromNodeRequestDtoTargetToolType,
      title: t(tool.title),
      icon: tool.icon,
      color: category.colors.dashboardBg,
      category: t(category.categoryTitle),
    })),
  );

  const toolsByCategory = allTools
    .filter((tool) => apiAvailableTools.includes(tool.id))
    .reduce(
      (acc, tool) => {
        if (!acc[tool.category]) {
          acc[tool.category] = [];
        }
        acc[tool.category].push(tool);
        return acc;
      },
      {} as Record<string, typeof allTools>,
    );

  //genTool.id and genTool.type and genTool.status is in the object
  const GeneratedToolInfo = () => {
    const generatedToolDetails = allTools.find(
      (tool) => tool.id === selectedNodeData.data.toolInfo?.toolType,
    );

    return generatedToolDetails &&
      selectedNodeData.data.toolInfo?.status !== "FAILED" ? (
      <div
        key={generatedToolDetails.id}
        className="flex items-center space-x-2 p-2 rounded w-full"
      >
        {selectedNodeData.data.toolInfo?.status === "PROCESSING" ? (
          <div className="w-full p-6 rounded-2xl ">
            <div className="flex flex-col items-center justify-center gap-6">
              <div className="w-full max-w-md">
                <LoadingContainer
                  title={t("mindmap.stepGeneratingTool")}
                  themeColor="blue"
                  fullHeight={false}
                />
              </div>
            </div>
          </div>
        ) : (
          selectedNodeData.data.toolInfo && (
            <DetailCard tool={selectedNodeData.data.toolInfo} />
            // expand icon button
            // <Button variant="ghost" size="sm">
            //   <ExpandIcon size={18} />
            // </Button>
          )
        )}
      </div>
    ) : null;
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeDescriptionModal}
      />
      {/* Modal Content */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg p-6 w-11/12 max-w-5xl flex flex-col space-y-4 max-h-[80vh]">
        <div className="flex justify-between items-center">
          {/* Node Title */}
          <>
            <h3 className="font-bold text-xl dark:border-gray-600">
              {selectedNodeData.data.label}
            </h3>
            {/* Description Section */}
            {selectedNodeData.data.description && (
              <div className="pr-2">
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <ReactMarkdown>
                    {selectedNodeData.data.description}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </>
          {/* Close Button */}
          <Button
            onClick={closeDescriptionModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500" />
          </Button>
        </div>

        {/* Generated Tools Section */}
        {selectedNodeData.data.toolInfo?.id && (
          <div className="border-t dark:border-gray-600 flex-1 overflow-auto modal-content gap-2 custom-scrollbar">
            <GeneratedToolInfo />
          </div>
        )}

        {/* Available Tools Section */}
        {!selectedNodeData.data.toolInfo?.id && (
          <div className="overflow-y-auto pr-2 border-t pt-4 dark:border-gray-600 flex-grow">
            <Text variant="h4" className="font-semibold text-lg mb-3">
              mindmap.availableTools
            </Text>
            <div className="overflow-y-auto custom-scrollbar gap-2">
              {Object.entries(toolsByCategory).map(([category, tools]) => (
                <div key={category} className="mb-6">
                  <Text
                    variant="h4"
                    className="mb-3 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {category}
                  </Text>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {tools.map((tool) => (
                      <div
                        key={tool.id}
                        className={`flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer ${
                          //if tool is generating disable all buttons
                          generatingToolId
                            ? "cursor-not-allowed opacity-50"
                            : ""
                        }`}
                        onClick={() =>
                          generateToolForNode(selectedNodeData.id, tool.id)
                        }
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div
                            className={`${tool.color} p-2 rounded-lg text-white flex-shrink-0`}
                          >
                            {generatingToolId?.type === tool.id ? (
                              <LoaderPinwheel
                                size={18}
                                className="animate-spin"
                              />
                            ) : (
                              <tool.icon size={18} />
                            )}
                          </div>
                          <span className="truncate">{tool.title}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
